<template>
  <div class="crppper-modal">
    <div class="upload">
      <label for="file-input" class="img-wrap" v-show="!imgData.src">
        <div class="img-wrap-placeholder">
          <div class="image-placeholder">
            <p class="w-100">Click here to select an image</p>
            <el-button size="large" @click="fileInput.click()">
              <svg-icon name="plus" />
              Select image
            </el-button>
          </div>
          <input
            id="file-input"
            class="avatar-input"
            type="file"
            ref="fileInput"
            @change="loadImage($event)"
            accept="image/png, image/jpeg"
            hidden
          />
        </div>
      </label>

      <cropper
        :class="[imgData.src && 'vue-advanced-cropper-class']"
        ref="cropperRef"
        :default-size="defaultSize"
        @change="onChange"
        :canvas="{
          minWidth: 256, // Minimum width
          minHeight: 256, // Minimum height
          maxWidth: 1600, // Maximum width
          maxHeight: 1600, // Maximum height
        }"
        :src="imgData.src"
        :stencil-props="{
          aspectRatio: currentAspectRatio
        }"
        :stencil-component="props.useType === 'profile' ? CircleStencil : undefined"
      />
    </div>

    <div class="tools" v-if="imgData.src">
      <div class="t-desc">zoom</div>
      <i :class="[scaleCurrent === 0 && 'disabled-i']" @click="handleScaleValue(1)"><svg-icon name="less" /></i>
      <el-slider v-model="scaleCurrent" :step="1" :max="5" :show-tooltip="false"   @change="checkScaleCurrent"/>
      <i :class="[scaleCurrent === 5 && 'disabled-i']" @click="handleScaleValue(0)"><svg-icon name="plus" /></i>
      <div class="tools-right">
        <div class="t-desc">Rotate</div>
        <div class="icon" @click="handleRotate(-90)"><svg-icon name="rotate" /></div>
        <div class="icon" @click="handleRotate(90)"><svg-icon name="rotate-right" /></div>
      </div>
      <div class="tools-right" v-if="aspectRatio === 100">
        <div class="t-desc">Resize</div>
        <div class="resize-btn" :class="{ active: currentAspectRatio === 1 }" @click="handleResize(1)">1:1</div>
        <div class="resize-btn" :class="{ active: currentAspectRatio === 4/3 }" @click="handleResize(4/3)">4:3</div>
        <div class="resize-btn" :class="{ active: currentAspectRatio === 3/4 }" @click="handleResize(3/4)">3:4</div>
      </div>
    </div>

    <p class="err-text" v-if="avatarErr">{{ avatarErr }}</p>

    <footer class="upload-footer">
      <el-button size="large" @click="fileInput.click()">Change image</el-button>
      <el-button size="large" type="primary" @click="save()" :is_disabled="!imgData.src">Save</el-button>
    </footer>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { Cropper, CircleStencil  } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import getMimeType from '@/utils/getMimeType';
import { ElButton, ElSlider } from 'element-plus';

const props = defineProps({
  maxSize: {
    type: Number,
    default: 1024, // 1024kb = 1MB
  },
  // aspect ratio
  // 1/1, 4/3, 16/9, or 100, 100 is 'resize'.
  aspectRatio: {
    type: Number,
    default: null, // null means no aspect ratio restriction
  },
  useType:{
    type: String,
    default: 'profile', // 'profile' or 'collection'
  }
});

const emit = defineEmits(['save']);

const fileInput = ref(null);
const avatarErr = ref('');
const scaleCurrent = ref(0);
const oldScaleCurrent = ref(0);
const currentAspectRatio = ref(1); // 默认1:1比例
const imgData = ref({
  src: null,
  data: null,
  type: null,
});

const cropperRef = ref(null); // NOTE: the name cannot be cropper

const checkScaleCurrent=(val)=>{

  if(scaleCurrent.value === oldScaleCurrent.value){
    return;
  }
  if (scaleCurrent.value > oldScaleCurrent.value) {
    handleZoomFun(1);
  } else {
    handleZoomFun(0);
  }
  oldScaleCurrent.value = scaleCurrent.value;
}

watch(currentAspectRatio, () => {
  if (cropperRef.value && imgData.value.src) {
    setTimeout(() => {
      cropperRef.value.setCoordinates(({ imageSize }) => {
        const aspectRatio = currentAspectRatio.value;
        
        let width, height;
        
        if (imageSize.width / aspectRatio <= imageSize.height) {
          width = imageSize.width;
          height = imageSize.width / aspectRatio;
        } else {
          height = imageSize.height;
          width = imageSize.height * aspectRatio;
        }
        
        return {
          width: Math.floor(width),
          height: Math.floor(height),
          left: (imageSize.width - width) / 2,
          top: (imageSize.height - height) / 2,
        };
      });
    }, 50);
  }
});

const handleScaleValue = (val) => {
  if (val && scaleCurrent.value > 0) {
    scaleCurrent.value--;
    checkScaleCurrent();
  } else if (!val && scaleCurrent.value < 5) {
    scaleCurrent.value += 1;
    checkScaleCurrent();
  };
  
};

const handleZoomFun = (val) => {
  let value = false;
  if (scaleCurrent.value > 0 && scaleCurrent.value < 5) {
    if (val) {
      value = (val + scaleCurrent.value * 0.2).toFixed(1);
    } else {
      value = updateScaleFactor(scaleCurrent.value).toFixed(1);
    }
  } else if (scaleCurrent.value === 5) {
    value = 2;
  } else if (scaleCurrent.value === 0) {
    value = 0;
  }

  if (value !== false) {
    cropperRef.value.zoom(value);
  }
};


const updateScaleFactor = (scale) => {
  const scaleMap = {
    4: 0.9,
    3: 0.8,
    2: 0.7,
    1: 0.6,
  };
  return scaleMap[scale] || 0;
};

const handleRotate = (val) => {
  cropperRef.value.rotate(val);
};

const handleResize = (aspectRatio) => {
  currentAspectRatio.value = aspectRatio;
};

const loadImage = (event) => {
  const { files } = event.target;

  if (files && files[0]) {
    avatarErr.value = ''; // reset error message

    // check file type
    const isJpgOrPng = files[0].type === 'image/jpeg' || files[0].type === 'image/png';

    // use props.maxSize to limit the file size
    const isLt2M = files[0].size / 1024 / 1024 < props.maxSize / 1024; // file size less than 1MB or custom size

    if (!isJpgOrPng) {
      avatarErr.value = 'Only JPG or PNG images are allowed.';
      return false;
    }

    if (!isLt2M) {
      avatarErr.value = `Image cannot exceed ${props.maxSize / 1024}MB.`;
      return false;
    }

    if (imgData.value.src) {
      URL.revokeObjectURL(imgData.value.src);
    }

    const blob = URL.createObjectURL(files[0]);
    const reader = new FileReader();
    reader.onload = (e) => {
      imgData.value.src = blob;
      imgData.value.data = files[0];
      imgData.value.type = getMimeType(e.target.result, files[0].type);
    };
    reader.readAsArrayBuffer(files[0]);
  }
};

const defaultSize = ({ imageSize }) => {
  const aspectRatio = currentAspectRatio.value;
  
  if (!aspectRatio) {
    const size = Math.min(imageSize.height, imageSize.width);
    return {
      width: size,
      height: size,
    };
  }
  
  let width, height;
  
  if (imageSize.width / aspectRatio <= imageSize.height) {
    width = imageSize.width;
    height = imageSize.width / aspectRatio;
  } else {
    height = imageSize.height;
    width = imageSize.height * aspectRatio;
  }
  
  return {
    width: Math.floor(width),
    height: Math.floor(height),
  };
};

// const onChange = (result) => {
// };

const save = () => {
  const { canvas } = cropperRef.value.getResult();
  canvas.toBlob((blob) => {
    imgData.value.data = blob;
    imgData.value.src = URL.createObjectURL(blob);
    emit('save', imgData.value);
  }, imgData.value.type);
};

const start = () => {
  fileInput.value.click();
};

onMounted(() => {
  if (props.aspectRatio && props.aspectRatio !== 100) {
    currentAspectRatio.value = props.aspectRatio === 100 ? 1 : props.aspectRatio;
  }

  start();
});
</script>

<style lang="scss">
.crppper-modal {
  .img-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    margin: 35px auto;
    height: 300px;
    border: 2px dashed var(--Black-50);
    border-radius: 5px;
    position: relative;
    cursor: pointer;

    .image-container img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .image-placeholder {
      display: block;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      text-align: center;
      gap: 16px;

      svg {
        fill: #555;
        width: 12px;
        height: 12px;
        margin-right: 8px;
      }

      p {
        font-size: 16px;
        color: #555;
        margin-bottom: 12px;
      }
    }

    .avatar-input {
      display: none;
    }

    @media (max-width: $mobile_size) {
      width: 90%;
    }
  }

  .vue-advanced-cropper {
    position: absolute;
    left: 0;
    top: 0;

    &.vue-advanced-cropper-class {
      height: 370px;
      width: 100%;
      background-color: var(--Black-50);
    }
  }
}

.crppper-modal {
  .upload {
    width: 100%;
    height: 370px;
    position: relative;
    background-color: var(--Bg-01);
    overflow: hidden;

    .loading {
      position: absolute;
    }
  }

  .err-text {
    font-size: 16px;
    padding: 16px 16px 0 16px;
    color: var(--Red-30);
  }

  .upload-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 24px 16px;
    gap: 8px;
  }

  .tools {
    display: flex;
    align-items: center;
    border: solid 1px var(--Black-50);

    .t-desc {
      margin-left: 16px;
    }

    .tools-right {
      display: flex;
      align-items: center;
      border-left: solid 1px var(--Black-50);
      height: 100%;
      padding: 8px;
      margin-right: 16px;

      .t-desc {
        margin-left: 4px;
        margin-right: 12px;
      }
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    .disabled-i{
      opacity: 0.5;
      pointer-events: none;
    }

    .icon {
      height: 24px;
      width: 24px;
      min-width: 24px;
      border-radius: 50%;
      border: 1px solid var(--PB-100);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 10px;
      color: var(--PB-100);

      &:first-child {
        margin-right: 10px;
      }

      &:last-child {
        margin-left: 10px;
      }

      &.active {
        background-color: var(--PB-100);
        color: white;
      }

      svg {
        height: 14px;
        width: 14px;
        fill: var(--PB-100);
      }
    }

    .resize-btn {
      height: 24px;
      min-width: 32px;
      padding: 0 8px;
      border-radius: 12px;
      border: 1px solid var(--PB-100);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 10px;
      color: var(--PB-100);
      margin: 0 2px;

      &.active {
        background-color: var(--PB-100);
        color: white;
      }

      &:hover {
        background-color: var(--PB-100);
        color: white;
      }
    }

    i {
      cursor: pointer;
      position: relative;
      top: 2px;

      svg {
        height: 16px;
        width: 16px;
        fill: var(--PB-100);
        margin: 8px;
      }
    }

    .el-slider {
      flex: 1;
      position: relative;
      margin: 0 0 0 8px;
    }

    @media (max-width: $mobile_size) {
      flex-wrap: wrap;

      .tools-right {
        width: 100%;
        border-top: solid 1px var(--Black-50);
        margin: 0;
        justify-content: space-around;

        .resize-btn {
          margin: 0 1px;
          min-width: 28px;
          font-size: 9px;
        }
      }
    }
  }
}
</style>
